import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { staffApi } from "../api/staffApi";
import { businessUnitsApi } from "../api/businessUnitsApi";
import { departmentsApi } from "../api/departmentsApi";
import { shrinkPhoto } from "./shrinkPhoto";
import { s3ProgressConfig } from "./uploadProgress";
import { uploadStaffProfilePic } from "../features/staff/staffSlice";

/**
 * Where a record's files are stored. Business units and departments have
 * their own folders and endpoints; anything without a store (parties, and
 * every file uploaded before these existed) lives with member photos.
 */
export type RecordStore = "businessunit" | "department";

const STORE_API: Record<
  RecordStore,
  {
    initUpload: (ids: string[], groupId?: string) => Promise<any>;
    getMediaLink: (
      ids: string[],
      size?: number,
      groupId?: string,
    ) => Promise<any>;
  }
> = {
  businessunit: businessUnitsApi,
  department: departmentsApi,
};

/** Uploads one file to `store` (or the member store) and returns its id. */
async function uploadToStore(
  file: File,
  groupId?: string | null,
  store?: RecordStore,
): Promise<string> {
  if (!store) return uploadStaffProfilePic(file, groupId || undefined);
  const id = uuidv4();
  const init = await STORE_API[store].initUpload([id], groupId || undefined);
  const url = init?.links?.[0]?.m;
  if (!url) throw new Error("No upload link was issued.");
  // Reports bytes sent, so the panel's progress rows move.
  await axios.put(url, file, {
    ...s3ProgressConfig(file),
    headers: { "Content-Type": file.type },
  });
  return id;
}

/** A stored picture reference: "{store}:{id}" for a store, a bare id otherwise. */
const parsePicture = (value: string): { store?: RecordStore; id: string } => {
  const [head, ...rest] = value.split(":");
  return head === "businessunit" || head === "department"
    ? { store: head, id: rest.join(":") }
    : { id: value };
};

/**
 * Photos and documents for records that have no media pipeline of their own
 * on the server — parties, business units, departments. The list travels on
 * the record as JSON (`mediaJson`), its picture as an object-store id
 * (`profilePic`), while the files themselves go through the
 * organisation-scoped upload and link endpoints that member photos use. `url`
 * is resolved on read and never stored.
 */
export type RecordMedia = {
  /** The file's id in the object store. */
  id: string;
  name: string;
  /** Mime type. */
  type: string;
  size: number;
  isCover?: boolean;
  /** Where the file is stored; absent for the member store. */
  store?: RecordStore;
  url?: string;
};

export function parseRecordMedia(mediaJson?: string | null): RecordMedia[] {
  if (!mediaJson) return [];
  try {
    const list = JSON.parse(mediaJson);
    return Array.isArray(list) ? list.filter((m) => m && m.id) : [];
  } catch {
    return [];
  }
}

/** What is stored: everything but the short-lived link. */
export const serialiseRecordMedia = (medias: RecordMedia[]) =>
  JSON.stringify(
    medias.map(({ id, name, type, size, isCover, store }) => ({
      id,
      name,
      type,
      size,
      ...(isCover ? { isCover: true } : {}),
      ...(store ? { store } : {}),
    })),
  );

/** Uploads the files and returns their entries, each with a local preview
 *  link so the panel can show them straight away. */
export async function uploadRecordFiles(
  files: File[],
  groupId?: string | null,
  store?: RecordStore,
): Promise<RecordMedia[]> {
  return Promise.all(
    files.map(async (original) => {
      const file = await shrinkPhoto(original);
      return {
        id: await uploadToStore(file, groupId, store),
        name: original.name,
        type: file.type || "application/octet-stream",
        size: file.size,
        ...(store ? { store } : {}),
        url: URL.createObjectURL(file),
      };
    }),
  );
}

/** What a record needs to carry for its picture and files to resolve. */
export type MediaCarrier = {
  /** Omitted where the record has none: the server then uses the caller's. */
  groupId?: string | null;
  profilePic?: string | null;
  mediaJson?: string | null;
};

export type WithMedia<T> = T & {
  profilePicUrl: string;
  medias: RecordMedia[];
  coverUrl?: string;
};

/**
 * Adds `profilePicUrl`, `medias` (with links) and `coverUrl` to every record.
 * One link request per organisation covers a whole page of records.
 */
export async function withRecordMedia<T extends MediaCarrier>(
  records: T[],
): Promise<WithMedia<T>[]> {
  const parsed = records.map((record) => ({
    record,
    medias: parseRecordMedia(record.mediaJson),
  }));
  // Links keyed "{store}:{id}" ("member:{id}" for the member store).
  const links = new Map<string, string>();
  const linkKey = (store: string, id: string) => `${store}:${id}`;
  // One lookup per organisation and store: a Super Admin's list can span
  // several organisations, and older files live in the member store.
  const batches = new Map<
    string,
    { groupId: string; store: string; ids: string[] }
  >();
  const want = (groupId: string, store: string, id: string) => {
    const key = `${groupId}|${store}`;
    const batch = batches.get(key) ?? { groupId, store, ids: [] };
    batch.ids.push(id);
    batches.set(key, batch);
  };
  parsed.forEach(({ record, medias }) => {
    const group = record.groupId ?? "";
    medias.forEach((m) => want(group, m.store ?? "member", m.id));
    if (record.profilePic && !record.profilePic.startsWith("http")) {
      const pic = parsePicture(record.profilePic);
      want(group, pic.store ?? "member", pic.id);
    }
  });
  await Promise.all(
    [...batches.values()].map(async ({ groupId, store, ids }) => {
      try {
        // Size 1: files are uploaded as the "m" variant, and the link has to
        // ask for the variant that was written or it points at nothing.
        const res =
          store === "member"
            ? await staffApi.getMediaLink(ids, 1, groupId || undefined)
            : await STORE_API[store as RecordStore].getMediaLink(
                ids,
                1,
                groupId || undefined,
              );
        res?.links?.forEach((l: { id: string; link: string }) =>
          links.set(linkKey(store, String(l.id)), l.link),
        );
      } catch (err) {
        console.error("Record media link resolution failed:", err);
      }
    }),
  );
  return parsed.map(({ record, medias }) => {
    const resolved = medias.map((m) => ({
      ...m,
      url: links.get(linkKey(m.store ?? "member", m.id)) ?? "",
    }));
    const pic = record.profilePic;
    const parsedPic = pic && !pic.startsWith("http") ? parsePicture(pic) : null;
    return {
      ...record,
      profilePicUrl: !pic
        ? ""
        : pic.startsWith("http")
          ? pic
          : (links.get(linkKey(parsedPic?.store ?? "member", parsedPic!.id)) ??
            ""),
      medias: resolved,
      coverUrl: resolved.find((m) => m.isCover)?.url || undefined,
    };
  });
}

/** Uploads a cropped picture; returns its object-store id. */
/** Uploads a record's picture; stored as "{store}:{id}" when it has a store. */
export const uploadRecordPicture = async (
  file: File,
  groupId?: string | null,
  store?: RecordStore,
) => {
  const id = await uploadToStore(file, groupId, store);
  return store ? `${store}:${id}` : id;
};
